import { useCallback, useState } from 'react';
import { postingLogApi } from '@/services/postingLogApi';
import { getApiErrorStatus } from '@/services/reportPaging';
import { toast } from 'sonner';
import { describeRetryError } from '@/utils/PostingLog/mappers';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { resendMockPostingLogLine } from '@/data/mockPostingLogData';

interface UsePostingLogRetryParams {
  allowRetry: boolean;
  integrationConfigured: boolean;
  onRetried: () => void;
}

export function usePostingLogRetry({ allowRetry, integrationConfigured, onRetried }: UsePostingLogRetryParams) {
  const { isDemo } = useScreenDataMode('reports-posting-log');
  const [retryingIds,      setRetryingIds]      = useState<Set<string>>(new Set());

  // Confirmation dialog
  const [retryDialogOpen, setRetryDialogOpen] = useState(false);
  const [retryTarget,     setRetryTarget]     = useState<string[]>([]);

  const resendLine = useCallback(async (rowId: string): Promise<void> => {
    if (isDemo) {
      const result = resendMockPostingLogLine(Number(rowId));
      if (!result.success) throw new Error(result.errors[0] ?? 'Retry failed');
      return;
    }
    await postingLogApi.resend(Number(rowId));
  }, [isDemo]);

  const handleBulkRetry = useCallback((logIds: number[]) => {
    if (logIds.length === 0 || !allowRetry || !integrationConfigured || retryingIds.size > 0) return;
    setRetryTarget([...new Set(logIds)].map(String));
    setRetryDialogOpen(true);
  }, [allowRetry, integrationConfigured, retryingIds]);

  const handleRetryConfirm = useCallback(async () => {
    const ids = retryTarget;
    if (ids.length === 0 || !allowRetry || !integrationConfigured || retryingIds.size > 0) return;

    setRetryingIds(new Set(ids));
    if (ids.length === 1) {
      const id = ids[0];
      try {
        await resendLine(id);
        toast.success('Line posted successfully');
      } catch (err) {
        toast.error('Retry failed', { description: describeRetryError(err) });
      }
    } else {
      let successCount = 0;
      let lastError: string | undefined;
      for (const id of ids) {
        try {
          await resendLine(id);
          successCount++;
        } catch (err) {
          lastError = describeRetryError(err);
          if (getApiErrorStatus(err) === 503) break;
        }
      }
      const summary = `${successCount} of ${ids.length} lines posted successfully`;
      if (successCount === ids.length) toast.success(summary);
      else toast.error(summary, { description: lastError });
    }
    setRetryingIds(new Set());
    setRetryTarget([]);
    onRetried();
  }, [retryTarget, allowRetry, integrationConfigured, retryingIds, resendLine, onRetried]);

  return {
    retryingIds, retryDialogOpen, setRetryDialogOpen, retryTarget,
    handleBulkRetry, handleRetryConfirm,
  };
}
